<script setup lang="ts">

import ItemV3RemoveGroupModal from "@/components/itemV3/parameter/itemV3RemoveGroupModal.vue";
import {computed, inject, ref, watch} from "vue";
import {useI18n} from "vue-i18n";

const {t} = useI18n();

const emit = defineEmits(["patch", "update:activeGroupIdx", "groups"]);

const props = withDefaults(defineProps<{
  stoneGroups: {}[]
  activeGroupIdx?: number;
}>(), {
  activeGroupIdx: -1,
});

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * stone groups
 *
 * Transferred from itemV3Grooves.vue's "grooves" computed: scans the current item's
 * variants for idx patterns "stones.<n>.<attr>" (ignoring the "add"/"remove" actions)
 * and groups them by <n> into one object per stone group.
 */

const stoneGroupList = computed(() => Object.values(props.stoneGroups))

const hasStoneGroups = computed(() => Object.keys(props.stoneGroups).length > 0)

// Let the parent (itemV3Stones.vue) know about the current stone groups, so it can
// build the "stone group list switch-button bar" and render the matching details.
watch(stoneGroupList, (value) => {
  emit('groups', value)
}, {immediate: true, deep: true})

/**
 * images
 */

const getStoneGroupLayoutImageUrl = (layoutType: string, size: 300 | 600 = 600) => {
  return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/stone_layout/${size}/${layoutType}.webp`
}

/**
 * group title
 *
 * The group title is related to the group's stoneLayout (e.g. "Steinreihe #1",
 * "Sternenhimmel #3") - each layout has its own text stored in de.json, falling back
 * to the generic "Stein-Gruppe #n" text when the layout has no dedicated entry.
 */

const stoneGroupNameTypes = ["single", "row", "crown", "starry_sky"]

const getStoneGroupTitle = (group: any) => {
  const key = stoneGroupNameTypes.includes(group.stone_layout) ? group.stone_layout : "default"
  return t('components.parameters.stoneGroupNames.' + key, {n: group.group_idx + 1})
}

/**
 * select / edit a stone group
 */

const selectStoneGroup = (groupIdx: number) => {
  emit('update:activeGroupIdx', groupIdx)
}

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  value = (typeof value == "string") ? value : value.idx
  emit('patch', {
    action: actionIdx,
    value: value,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * remove stone group
 */

const showRemoveModal = ref(false)
const stoneGroupToRemove = ref<any>(null)

const confirmRemoveStoneGroup = (group: any) => {
  stoneGroupToRemove.value = group
  showRemoveModal.value = true
};

const stoneGroupToRemoveName = computed(() => {
  return stoneGroupToRemove.value ? getStoneGroupTitle(stoneGroupToRemove.value) : ''
})

const handleRemoveStoneGroup = () => {
  if (stoneGroupToRemove.value) {
    handleValuePatch(`stones.${stoneGroupToRemove.value.group_idx}.remove`, 'remove')
    if (props.activeGroupIdx === stoneGroupToRemove.value.group_idx) {
      emit('update:activeGroupIdx', -1)
    }
  }
  showRemoveModal.value = false
  stoneGroupToRemove.value = null
};

</script>

<template>
  <div class="mt-4 w-full">
    <ul class="mt-1 flex flex-col gap-y-2">
      <li v-for="(group, groupKey) in stoneGroups" :key="'stone-group-' + groupKey"
          class="flex items-center gap-3 rounded-2xl p-2 transition-colors hover:bg-[#f3f0e9] bg-white border cursor-pointer"
          :class="activeGroupIdx === group.group_idx ? 'border-brand-main bg-[#f3f0e9]' : 'border-gray-200'"
      >
        <span
            class="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-[#e7ddcc] bg-[#f8f5ee]"
            @click="selectStoneGroup(group.group_idx)"
        >
          <img
              :src="getStoneGroupLayoutImageUrl(group.stone_layout, 300)"
              :srcset="getStoneGroupLayoutImageUrl(group.stone_layout, 600)"
              class="w-full h-full object-cover" alt=""/>
        </span>
        <div
            class="min-w-0 flex-1"
            @click="selectStoneGroup(group.group_idx)"
        >
          <span class="block text-sm font-medium tracking-[0.1px] text-parameter-title">{{ getStoneGroupTitle(group) }}</span>
          <span class="block truncate text-xs leading-[15px] text-parameter-text">
          {{ group.stone_setting }}
        </span>
          <span class="block truncate text-xs leading-[15px] text-parameter-text">
          {{ group.stone_size }} {{ group.stone_quality }}
        </span>
        </div>
        <button
            type="button"
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-[#8f8a80] transition-colors hover:bg-white hover:text-brand-main"
            title="Bearbeiten"
            @click="selectStoneGroup(group.group_idx)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z"></path>
          </svg>
        </button>
        <button
            type="button"
            class="flex size-8 shrink-0 items-center justify-center rounded-full text-[#8f8a80] transition-colors hover:bg-white hover:text-red-500"
            title="Entfernen"
            @click="confirmRemoveStoneGroup(group)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="size-3.5" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </li>
    </ul>
  </div>

  <!-- Remove Stone Group Confirmation Modal -->
  <item-v3-remove-group-modal v-model="showRemoveModal" :item-name="stoneGroupToRemoveName" @confirm="handleRemoveStoneGroup"/>
</template>
